import type { CSSProperties, JSXElementConstructor, ReactNode } from 'react';
import type { AlignItems, JustifyContent, TextAlign } from 'types/css';
import type { IconType } from './icons';
import type { TypeButtonProps } from './molecules/card-banner';

export type CardSize = 'xs' | 's' | 'm' | 'l' | 'xl' | 'full';
export type CardFlatSize = 'flat-m' | 'flat-xl';

export type TypeSrcSetImage = {
  /**
   * Media Query de la imagen
   */
  media: string;
  /**
   * Define la ruta de la imagen
   */
  src: string;
};

export type TypeImage = {
  /**
   * Define la ruta de la imagen
   */
  src: string;
  /**
   * Define el texto alternativo de la imagen
   */
  alt?: string;
};

export type WPImage = {
  link: string;
  /**
   * Define el texto alternativo de la imagen
   */
  sourceUrl: string;
  altText?: string;
};

export interface TypeImageComplete extends TypeImage {
  /**
   * Define el width de la imagen
   */
  width: string | number;
  /**
   * Define el texto alternativo de la imagen
   */
  height: string | number;
}

export interface TypeImageSrcSet extends TypeImage {
  /**
   * Array de TypeSrcSetImage
   */
  srcset: TypeSrcSetImage[];
}

export type TypeStyle = {
  /**
   * Estilos CSS para el componente
   */
  style?: CSSProperties;
};

export type TypeClassName = {
  /**
   * Permite añadir clases extras
   */
  className?: string;
};

export type TypeChildren = {
  /**
   * Permite añadir cualquier tipo de children
   */
  children: ReactNode | ReactNode[];
};

export type TypeChildrenOptional = {
  /**
   * Permite añadir cualquier tipo de children
   */
  children?: ReactNode | ReactNode[];
};

export type TypeAs = {
  // TODO: Añadir tipos, sustituir por generic cuando podamos tipar el useRef
  /**
   * Etiqueta html que utilizará el componente
   */
  as?: React.ElementType<any>;
};

export type TypeWrapper = {
  /**
   * Permite añadir clases adicionales al wrapper
   */
  classNameWrapper?: string;
  /**
   * Tamaño del wrapper
   */
  wrapperSize?: string;
  /**
   * ¿Quieres activar el tamaño máximo?
   */
  wrapper?: boolean;
};

export type TypeBackgroundImage = {
  /**
   * Permite definir una imagen de fondo
   */
  backgroundImage?: string | TypeImageSrcSet;
  /**
   * Permite oscurecer la imagen de fondo
   */
  overlay?: number;
  notLazy?: boolean;
  priority?: boolean;
};

export interface TypeBackground extends TypeBackgroundImage {
  /**
   * Permite añadir un color de fondo
   */
  backgroundColor?: string;
}

export type TypeAlignContent = {
  /**
   * Permite alinear el contenido horizontalmente
   */
  justify?: JustifyContent | string;
  /**
   * Permite alinear el contenido verticalmente
   */
  align?: AlignItems | string;
  /**
   * Permite alinear el texto
   */
  textAlign?: TextAlign | string;
};

export type TypeLink = {
  /**
   * Define el texto del link
   */
  label: string;
  /**
   * Define la ruta del link
   */
  href: string;
};

export interface TypeButtonLink extends TypeLink {
  /**
   * Define los estilos del botón
   */
  style: 'primary' | 'secondary' | 'tertiary';
  /**
   * El boton esta en negativo
   */
  negative?: boolean;
}

export interface TypeGenericMinimalProps
  extends TypeClassName,
    TypeBackground,
    TypeAlignContent {}

export interface TypeGenericProps extends TypeGenericMinimalProps {
  /**
   * Permite añadir un padding al contenedor
   */
  padding?: string;
}

export type TypeLineCustomTag = {
  value: string;
  opacity?: number;
};

export type TypeLineCustomTagProps = {
  /**
   * Permite añadir clases extras al componente.
   */
  className?: string;
  lineOne?: TypeLineCustomTag;
  lineTwo?: TypeLineCustomTag;
  lineThree?: TypeLineCustomTag;
  lineOptional?: TypeLineCustomTag;
};

export type WpSourceImage = {
  sourceUrl: string;
  altText: string;
};
export type WpImage = {
  imagen: WpSourceImage;
};

export interface LayoutImage extends WpImage {
  anchura: string;
}

export type srcSet = {
  media: string;
  src: string;
};

export type WpLink = {
  url: string;
};

export type WpButton = {
  link: WpLink;
  label: string;
  style: TypeButtonProps['style'];
  negativo: boolean;
  size: TypeButtonProps['size'];
};

export type WpProductDescriptionButton = {
  showIcon: boolean;
  link: WpLink;
  label: string;
  style: TypeButtonProps['style'];
  negativo: boolean;
  size: TypeButtonProps['size'];
};

export type AccesoryBlock = {
  titulo: string;
  link: WpButton;
};

export type TypeToastLink =
  | {
      type: 'url';
      label: string;
      url: string;
    }
  | {
      type: 'action';
      label: string;
      action: (...args: any[]) => void;
    };

export type TypeToast = {
  id: string;
  message: string;
  duration?: number;
  createdAt: number;
  visible: boolean;
  link?: TypeToastLink;
  backgroundColor: string;
  Icon?: JSXElementConstructor<IconType>;
};

export type TypeToastDispatch = {
  toast: (message: string, options: ToastOptions) => void;
  closeToast: (id: string) => void;
  deleteToast: (id: string) => void;
};

export type ToastOptions = Pick<
  TypeToast,
  'Icon' | 'backgroundColor' | 'duration' | 'link'
>;

export enum ActionType {
  ADD_TOAST,
  DISMISS_TOAST,
  REMOVE_TOAST,
}

export type ToastAction =
  | {
      type: ActionType.ADD_TOAST;
      toast: TypeToast;
    }
  | {
      type: ActionType.DISMISS_TOAST;
      toastId?: string;
    }
  | {
      type: ActionType.REMOVE_TOAST;
      toastId?: string;
    };
